import { userQueryKeys } from '@/config/queryKeys';
import { ROLE_HIERARCHY } from '@/lib/roles';
import { fetchAllUsers, fetchUserStudies } from '@/services/users';
import type { UserPublic, UserStudyPublic } from '@/types';
import { useQuery } from '@tanstack/react-query';

/** Infer primary org as first key in allowed_roles.
 * TODO: use primary_org_id from API when available */
export function inferPrimaryOrgId(user: UserPublic): string | undefined {
  return Object.keys(user.allowed_roles ?? {})[0];
}

/** Infer primary role as highest in ROLE_HIERARCHY within the primary org.
 * TODO: use primary_role from API when available */
export function inferPrimaryRole(user: UserPublic, orgId: string): string | undefined {
  const roles = user.allowed_roles?.[orgId] ?? [];
  return ROLE_HIERARCHY.find(r => roles.includes(r));
}

/** No `staleTime` override on purpose: a miss in this shared list is rendered as a definitive
 * "user not found", and the sites that add or re-role users invalidate other key roots
 * (`orgs/{id}/users`), so a long window would report a real user as nonexistent. */
export function useUserDetail(userId: string, enabled = true) {
  return useQuery({
    queryKey: userQueryKeys.list(),
    queryFn: fetchAllUsers,
    select: (users: UserPublic[]) => users.find(u => u.id === userId) ?? null,
    enabled,
  });
}

/** Fetch the studies a user has access to. */
export function useUserStudies(userId: string, enabled = true) {
  return useQuery<UserStudyPublic[]>({
    queryKey: userQueryKeys.studies(userId),
    queryFn: () => fetchUserStudies(userId),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}
